// ============================================================================
// PHI API — Client-side callable wrappers for PHI Cloud Functions
//
// All PHI reads/writes are routed through Cloud Functions (Admin SDK) so
// every access is role-gated and logged server-side. Direct Firestore PHI
// reads remain PERMISSION_DENIED from the client (Firestore rules, Step 1).
//
// Role gates are enforced server-side. The client does NOT need to pre-check
// roles before calling — the function will throw 'permission-denied' if the
// caller's role is not allowed.
//
// Usage pattern:
//   import { PHI_MODE } from '../config/featureFlags.js';
//   if (!PHI_MODE) throw new Error('PHI module not enabled');
//   const result = await getPatientIdentity(referralId);
//   // result = { exists: boolean, data: object | null }
//
// PHI_READ_ROLES  (server): tenant_admin, intake_coordinator, triage_nurse,
//                           scheduler, specialist
// PHI_WRITE_ROLES (server): tenant_admin, intake_coordinator, triage_nurse,
//                           scheduler
// ============================================================================

import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirebaseApp } from './store.firebase.js';

let _functions = null;

const getFunctionsInstance = () => {
  if (!_functions) {
    const app = getFirebaseApp();
    if (app) _functions = getFunctions(app);
  }
  return _functions;
};

const call = async (name, data) => {
  const fn = getFunctionsInstance();
  if (!fn) throw new Error('Firebase Functions not initialized');
  const result = await httpsCallable(fn, name)(data);
  return result.data;
};

// ── Patient Identity ──────────────────────────────────────────────────────────

/**
 * Read the patient_identity PHI document for a referral.
 * Writes a VIEW_PATIENT_IDENTITY phi_access_log entry server-side.
 * @param {string} referralId
 * @returns {Promise<{ exists: boolean, data: object|null }>}
 */
export const getPatientIdentity = (referralId) =>
  call('getPatientIdentity', { referralId });

/**
 * Create or update the patient_identity PHI document.
 * Writes an UPSERT_PATIENT_IDENTITY phi_access_log entry server-side.
 * @param {string} referralId
 * @param {{ name?: object, dob?: string, sex?: string|null, identifiers?: array }} payload
 * @returns {Promise<{ success: true }>}
 */
export const upsertPatientIdentity = (referralId, payload) =>
  call('upsertPatientIdentity', { referralId, payload });

// ── Contact ───────────────────────────────────────────────────────────────────

/**
 * Read the contact PHI document for a referral.
 * Writes a VIEW_CONTACT phi_access_log entry server-side.
 * @param {string} referralId
 * @returns {Promise<{ exists: boolean, data: object|null }>}
 */
export const getContact = (referralId) =>
  call('getContact', { referralId });

/**
 * Create or update the contact PHI document.
 * Writes an UPSERT_CONTACT phi_access_log entry server-side.
 * @param {string} referralId
 * @param {{ phone?: string|null, email?: string|null, address?: object|null }} payload
 * @returns {Promise<{ success: true }>}
 */
export const upsertContact = (referralId, payload) =>
  call('upsertContact', { referralId, payload });

// ── Insurance ─────────────────────────────────────────────────────────────────

/**
 * Read the insurance PHI document for a referral.
 * Writes a VIEW_INSURANCE phi_access_log entry server-side.
 * @param {string} referralId
 * @returns {Promise<{ exists: boolean, data: object|null }>}
 */
export const getInsurance = (referralId) =>
  call('getInsurance', { referralId });

/**
 * Create or update the insurance PHI document.
 * Writes an UPSERT_INSURANCE phi_access_log entry server-side.
 * @param {string} referralId
 * @param {{ payerName?: string|null, payerId?: string|null, memberId?: string|null,
 *           groupNumber?: string|null, planType?: string|null }} payload
 * @returns {Promise<{ success: true }>}
 */
export const upsertInsurance = (referralId, payload) =>
  call('upsertInsurance', { referralId, payload });
